import React, { useEffect } from "react";
import { Box, Fade, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface LightboxImage {
    src: string;
    title?: string;
    description?: string;
}

interface ImageLightboxProps {
    images: LightboxImage[];
    /** Índice da imagem aberta; null fecha o visualizador. */
    index: number | null;
    onChange: (index: number | null) => void;
}

const navButtonSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'common.white',
    bgcolor: 'rgba(0, 0, 0, 0.4)',
    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.6)' },
} as const;

/** Zoom da imagem sobre overlay escuro, com a legenda embaixo; navega entre as imagens da seção (setas ou teclado). */
export function ImageLightbox({ images, index, onChange }: ImageLightboxProps) {
    const open = index !== null && !!images[index];
    const hasNavigation = images.length > 1;

    const go = (step: number) => {
        if (index === null) return;
        onChange((index + step + images.length) % images.length);
    };

    useEffect(() => {
        if (!open || !hasNavigation) return;

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') go(-1);
            if (event.key === 'ArrowRight') go(1);
        };

        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    });

    const image = open ? images[index!] : null;

    return (
        <Modal
            open={open}
            onClose={() => onChange(null)}
            closeAfterTransition
            slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' } } }}
            aria-label={image?.title || 'Imagem ampliada'}
        >
            <Fade in={open}>
                <Box
                    onClick={() => onChange(null)}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 2, md: 6 },
                        outline: 'none',
                    }}
                >
                    <IconButton
                        aria-label="Fechar"
                        onClick={() => onChange(null)}
                        sx={{ ...navButtonSx, top: 16, right: 16, transform: 'none' }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {hasNavigation && (
                        <>
                            <IconButton
                                aria-label="Imagem anterior"
                                onClick={(e) => { e.stopPropagation(); go(-1); }}
                                sx={{ ...navButtonSx, left: { xs: 8, md: 24 } }}
                            >
                                <ChevronLeftIcon fontSize="large" />
                            </IconButton>
                            <IconButton
                                aria-label="Próxima imagem"
                                onClick={(e) => { e.stopPropagation(); go(1); }}
                                sx={{ ...navButtonSx, right: { xs: 8, md: 24 } }}
                            >
                                <ChevronRightIcon fontSize="large" />
                            </IconButton>
                        </>
                    )}

                    {image && (
                        <Box
                            component="figure"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{ m: 0, maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                            <Box
                                component="img"
                                src={image.src}
                                alt={image.title || ''}
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: (image.title || image.description) ? '75vh' : '85vh',
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    boxShadow: 8,
                                    display: 'block',
                                }}
                            />
                            {(image.title || image.description || hasNavigation) && (
                                <Box component="figcaption" sx={{ mt: 2, maxWidth: 800, textAlign: 'center', color: 'common.white' }}>
                                    {image.title && (
                                        <Typography variant="subtitle1" fontWeight={600}>{image.title}</Typography>
                                    )}
                                    {image.description && (
                                        <Typography variant="body2" sx={{ opacity: 0.85, whiteSpace: 'pre-line' }}>
                                            {image.description}
                                        </Typography>
                                    )}
                                    {hasNavigation && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.6 }}>
                                            {index! + 1} / {images.length}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
}
